<template>
  <v-col cols="12">
    <v-toolbar color="grey lighten-3">
      <v-toolbar-title>Series</v-toolbar-title>
      <v-spacer></v-spacer>

      <v-btn small fab color="primary" @click="onAddSeries()">
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </v-toolbar>

    <v-expansion-panels focusable>
      <draggable
        v-model="seriesList"
        @start="drag = true"
        @end="
          drag = false;
          onDragSeriesEnd();
        "
        handle=".draghandle"
        style="width: 100%"
      >
        <transition-group>
          <v-expansion-panel v-for="serie in seriesList" :key="serie.id">
            <!-- Expansion panel Header START -->
            <v-expansion-panel-header color="grey lighten-5">
              <template v-slot:default="{ open }">
                <v-fade-transition leave-absolute>
                  <v-row no-gutters align="center">
                    <v-col cols="1">
                      <v-btn icon class="draghandle">
                        <v-icon>mdi-drag-horizontal</v-icon>
                      </v-btn>
                    </v-col>
                    <!-- SERIES NAME -->
                    <v-col cols="4">
                      <span v-if="open" key="0">
                        {{ serie.name.defaultValue.value }}
                      </span>
                      <span v-else key="1">
                        {{ serie.name.defaultValue.value }}
                      </span>
                    </v-col>
                    <v-spacer />
                    <!-- SHOT COUNT -->
                    <v-col cols="1">
                      <span>{{
                        serie.shots.count != 0 ? serie.shots.count : '∞'
                      }}</span>
                    </v-col>
                    <!-- SERIES TYPE (MATCH/SIGHTING) -->
                    <v-col cols="2">
                      <v-chip
                        small
                        :color="serie.type === 'MATCH' ? 'primary' : 'inverted'"
                      >
                        {{ serie.type }}
                      </v-chip>
                    </v-col>
                    <!-- SERIES TIME -->
                    <v-col cols="3">
                      <div class="d-flex flew-row">
                        <div
                          v-for="procedure in serie.procedures.filter(
                            (p) =>
                              p.procedureSteps && p.procedureSteps.length > 0,
                          )"
                          :key="procedure.id"
                        >
                          Time: {{ getProcedureTimeString(procedure) }}
                          <v-tooltip
                            bottom
                            v-if="hasCalloutsConfigured(procedure)"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon v-bind="attrs" v-on="on"
                                >mdi-volume-low</v-icon
                              >
                            </template>
                            <span>Callouts configured</span>
                          </v-tooltip>
                        </div>
                      </div>
                    </v-col>
                    <!-- ACTION BUTTONS -->
                    <v-col cols="1">
                      <v-btn icon>
                        <v-icon @click="onCopySeries(serie)"
                          >mdi-content-copy</v-icon
                        >
                      </v-btn>
                      <v-btn icon>
                        <v-icon @click="onDeleteSeries(serie.id)"
                          >mdi-close-thick</v-icon
                        >
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-fade-transition>
              </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-tabs v-model="subTab" align-with-title>
                <v-tabs-slider color="blue"></v-tabs-slider>

                <v-tab
                  v-for="subGroupItem in subGroupItems"
                  :key="subGroupItem"
                >
                  {{ subGroupItem }}
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="subTab">
                <v-tab-item
                  v-for="(subGroupItem, subGroupIndex) in subGroupItems"
                  :key="subGroupIndex"
                  class="ma-4"
                >
                  <!-- GENERAL -->
                  <v-row v-if="subGroupIndex == 0">
                    <EditSeriesGeneralPanel
                      :serie="serie"
                      :profiles="profiles"
                      :scoringDefinitions="scoringDefinitions"
                      :teamShootingEnabled="teamShootingEnabled"
                    />
                  </v-row>

                  <!-- Time  -->
                  <v-row v-if="subGroupIndex == 1">
                    <EditSeriesTimePanel
                      :serie="serie"
                      :profiles="profiles"
                      :selectedAudioCollection="selectedAudioCollection"
                      :selectedAudioProfileId="selectedAudioProfileId"
                      :mediaTemplates="mediaTemplates"
                      :requiredTargetCount="requiredTargetCount"
                    />
                  </v-row>

                  <!-- Event templates  -->
                  <v-row v-if="subGroupIndex == 2">
                    <EditSeriesEventTemplatePanel :serie="serie" />
                  </v-row>

                  <v-row v-if="subGroupIndex == 3">
                    <EditSeriesTargetLift :serie="serie" :profiles="profiles" />
                  </v-row>
                  <v-row v-if="subGroupIndex == 4">
                    <EditSeriesScoringConvert :serie="serie" />
                  </v-row>
                  <v-row v-if="subGroupIndex == 5">
                    <EditSeriesApproval :serie="serie" />
                  </v-row>
                </v-tab-item>
              </v-tabs-items>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </transition-group>
      </draggable>
    </v-expansion-panels>
  </v-col>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';
import EditSeriesGeneralPanel from './SeriesPanel/EditSeriesGeneralPanel';
import EditSeriesTimePanel from './SeriesPanel/EditSeriesTimePanel';
import EditSeriesEventTemplatePanel from './SeriesPanel/EditSeriesEventTemplatePanel';
import EditSeriesTargetLift from './SeriesPanel/EditSeriesTargetLift';
import EditSeriesApproval from './SeriesPanel/EditSeriesApproval';
import EditSeriesScoringConvert from './SeriesPanel/EditSeriesScoringConvert';
import draggable from 'vuedraggable';
export default {
  name: 'EditSeriesPanel',
  components: {
    EditSeriesGeneralPanel,
    EditSeriesTimePanel,
    EditSeriesEventTemplatePanel,
    EditSeriesTargetLift,
    EditSeriesApproval,
    EditSeriesScoringConvert,
    draggable,
  },
  props: {
    series: {
      default: () => [],
      type: Array,
    },
    profiles: Array,
    scoringDefinitions: Array,
    sums: Array,
    sumSequences: Array,
    selectedAudioCollection: Object,
    selectedAudioProfileId: String,
    mediaTemplates: Array,
    mediaFiles: Array,
    teamShootingEnabled: Boolean,
    requiredTargetCount: {
      type: Number,
      default: null,
    },
  },
  data: () => ({
    subTab: null,
    subGroupItems: [
      'General',
      'Time',
      'Event templates',
      'Target Lift',
      ' Scoring convert',
      'Series approval',
    ],
    seriesList: [],
  }),

  created() {
    this.seriesList = this.series;
  },

  watch: {
    series: function (val) {
      if (val.length != this.seriesList.length) this.seriesList = val;
    },
  },

  methods: {
    onDragSeriesEnd() {
      this.$emit('onDragSeriesEnd', this.seriesList);
    },
    onAddSeries() {
      const id = uuidv4();
      const sumId = uuidv4();
      this.series.push({
        id: id,
        name: {
          defaultValue: {
            value: 'New series',
          },
          profileValues: [],
        },
        type: 'SIGHTING',
        marking: [
          {
            type: 'OPEN',
            client: 'E_MONITOR',
          },
          {
            type: 'OPEN',
            client: 'E_SPECTATOR',
          },
        ],
        shots: {
          count: 0,
        },
        eventTemplates: [],
        targetScoringDefinition: [],
        procedures: [
          {
            id: uuidv4(),
            type: 'SHOOTING_STAGE',
            executionCount: 1,
            procedureSteps: [],
          },
        ],
        targetLiftPosition: [],
        targetScale: {
          enabled: false,
          actualDistance: 0,
          nominalDistance: 0,
        },
        teamShootingSetup: {
          enableSighters: false,
          multipleShooters: false,
        },
      });

      //Create sums for the serie
      const sumLength = this.sums.length;
      let newSum = {
        id: sumId,
        innerHitCount: false,
        name: {
          defaultValue: {
            value: 'Sum ' + sumLength,
            languages: [],
          },
          profileValues: [],
        },
        numberOfDecimals: 0,
        scoreType: 'SCORE_POINTS',
        series: [
          {
            id: id,
            shots: null,
          },
        ],
        type: 'SERIES_TOTAL',
      };

      for (var i = 0; i < this.sums.length; i++) {
        if (this.sums[i].type == 'TOTAL') {
          this.sums[i].series.push({
            id: id,
            shots: null,
          });
        }
      }
      this.sums.push(newSum);

      if (this.sumSequences.length != 0) {
        this.sumSequences[0].sumIds.push(sumId);
      }
    },
    onDeleteSeries(id) {
      this.$emit('onDeleteSeries', id);
    },
    onCopySeries(serie) {
      this.$emit('onCopySeries', serie);
    },
    setTimeErrorTab() {
      this.subTab = 1;
    },
    getProcedureTimeString(procedure) {
      const countdown = procedure.procedureSteps.find(
        (s) => s.time.mode === 'COUNTDOWN_TIME',
      );
      const scoring = procedure.procedureSteps.find(
        (s) => s.time.mode === 'SCORING_TIME',
      );
      return (
        (countdown ? countdown.time.from.defaultValue.value + ' / ' : '') +
        (scoring ? scoring.time.from.defaultValue.value : '')
      );
    },
    hasCalloutsConfigured(procedure) {
      return (
        procedure &&
        procedure.procedureSteps &&
        procedure.procedureSteps.some(
          (p) => p.callouts && p.callouts.length > 0,
        )
      );
    },
  },
};
</script>
